import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["text", "listsPanel", "radioBtn", "textWrapper"];

  connect() {
    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    this.boundHandleScroll = this.handleScroll.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    window.addEventListener('scroll', this.boundHandleScroll, true);
    window.addEventListener('resize', this.boundHandleScroll);
  }

  disconnect() {
    if (this.boundHandleClickOutside) {
      document.removeEventListener('click', this.boundHandleClickOutside);
    }
    if (this.boundHandleScroll) {
      window.removeEventListener('scroll', this.boundHandleScroll, true);
      window.removeEventListener('resize', this.boundHandleScroll);
    }
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.closeDropdown();
    }
  }

  closeDropdown() {
    if (this.listsPanelTarget.tagName === 'DETAILS') {
      this.listsPanelTarget.open = false;
    } else {
      this.listsPanelTarget.classList.add("hidden");
    }
    
    if (this.hasTextWrapperTarget) {
      this.textWrapperTarget.classList.remove("open");
    }
    this.resetFloatingPanelStyles();
  }

  showSelectedValue(e) {
    this.textTarget.innerHTML = e.target.innerHTML // show selected value
    const radioBtn = this.radioBtnTargets[parseInt(e.target.dataset.index)];
    radioBtn.checked = true;
    
    // Trigger change event for multi-device controller to update current editing device
    const changeEvent = new Event('change', { bubbles: true });
    radioBtn.dispatchEvent(changeEvent);
    
    this.listsPanelTarget.open = !this.listsPanelTarget.open; // toggle lists panel
    
    // check if it is device type dropdown, if so, update device icon
    if (this.element.id === "device_type_id") {
      this.updateDeviceIcon(e.target.id)
    }
  }

  updateDeviceIcon(deviceTypeId) {
    // search device icon
    const deviceIcon = document.getElementById("device-icon")
    if (!deviceIcon) {
      return
    }
    
    // get device type name
    const deviceTypeName = this.getDeviceTypeName(deviceTypeId)
    const iconPath = this.getIconPath(deviceTypeName)
    
    // update device icon src
    deviceIcon.src = iconPath
  }

  getDeviceTypeName(deviceTypeId) {
    // get device type name from DOM
    const deviceTypeElement = document.getElementById(deviceTypeId)
    
    if (deviceTypeElement) {
      const dataAttribute = deviceTypeElement.getAttribute('data-device-type-name')
      const textContent = deviceTypeElement.textContent.trim()
      
      return dataAttribute || textContent
    }
    return null
  }

  getIconPath(deviceTypeName) {
    if (!deviceTypeName) return this.getAssetPath("ap")
    
    const normalizedName = deviceTypeName.toLowerCase().replace(/\s+/g, '-')
    
    switch (normalizedName) {
      case 'access-point':
        return this.getAssetPath("ap")
      case 'poe-switch':
      case 'switch':
        return this.getAssetPath("switch")
      case 'gateway':
        return this.getAssetPath("gateway")
      default:
        return this.getAssetPath("ap")
    }
  }

  getAssetPath(type) {
    // get correct asset path from hidden input element
    const pathsElement = document.getElementById("device-icon-paths")
    if (pathsElement) {
      const path = pathsElement.getAttribute(`data-${type}-path`)
      return path
    }
    
    // if cannot get path, use default path
    return `/assets/icons/device-placement-${type === 'ap' ? 'ap' : type}.svg`
  }

  toggle() {
    const shouldOpen = this.listsPanelTarget.classList.contains("hidden");
    this.listsPanelTarget.classList.toggle("hidden", !shouldOpen);
    if (this.hasTextWrapperTarget) {
      this.textWrapperTarget.classList.toggle("open", shouldOpen);
    }
    if (shouldOpen) {
      this.positionDropdown();
    }
  }

  toggleListsPanel() {
    this.listsPanelTarget.open = !this.listsPanelTarget.open;
  }

  select(event) {
    const el = event.currentTarget || event.target
    const name = el.dataset.displayText || el.innerText
    const url = el.dataset.url
    this.textTarget.textContent = name
    this.listsPanelTarget.classList.add("hidden")
    this.resetFloatingPanelStyles();

    if (url) window.location.href = url
  }

  positionDropdown() {
    const panel = this.listsPanelTarget;
    if (!panel || panel.classList.contains("hidden")) return;

    const wrapper = this.hasTextWrapperTarget ? this.textWrapperTarget : this.element;
    const rect = wrapper.getBoundingClientRect();
    const topologyRoot = this.element.closest('[data-controller*="topology-editor"]');
    const container = this.element.closest(".topology__container");
    const inTopologyPage = Boolean(topologyRoot);
    const useFloatingPanel = Boolean(container) || inTopologyPage;

    if (useFloatingPanel) {
      const minWidth = 220;
      const maxWidth = 420;
      const panelWidth = Math.min(Math.max(rect.width, minWidth), maxWidth);
      panel.style.position = "fixed";
      panel.style.left = `${Math.max(8, rect.left)}px`;
      panel.style.width = `${panelWidth}px`;
      panel.style.minWidth = `${panelWidth}px`;
      panel.style.maxWidth = `${panelWidth}px`;
      panel.style.right = "auto";
      panel.style.zIndex = "4000";
      panel.style.maxHeight = "320px";
    } else {
      this.resetFloatingPanelStyles();
    }

    if (useFloatingPanel) {
      // In topology floating mode, always open downward to avoid overlapping header/nav bar.
      panel.style.bottom = "auto";
      panel.style.top = `${Math.max(8, rect.bottom + 2)}px`;
    } else {
      panel.style.bottom = "auto";
      panel.style.top = "";
    }
  }

  resetFloatingPanelStyles() {
    if (!this.listsPanelTarget) return;
    const panel = this.listsPanelTarget;
    panel.style.position = "";
    panel.style.left = "";
    panel.style.top = "";
    panel.style.bottom = "";
    panel.style.width = "";
    panel.style.minWidth = "";
    panel.style.maxWidth = "";
    panel.style.right = "";
    panel.style.zIndex = "";
    panel.style.maxHeight = "";
  }

  handleScroll() {
    if (this.listsPanelTarget.classList.contains("hidden")) return;
    this.positionDropdown();
  }
}
